import {
  useCanvasCalendarNav,
  useScrollToNowKey,
  useScrollToTimeRequest,
} from "@/components/canvas/useCanvasCalendarNav.ts";
import type { CalendarEvent } from "@/types/events.ts";
import { parseISO } from "date-fns";
import {
  type ComponentProps,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { CalendarView } from "./CalendarView.tsx";
import { EditableCalendarView } from "./EditableCalendarView.tsx";

/** Grid props not owned by {@link CanvasCalendarBody} itself — the selection bundle
 *  from `useCalendarSurface` plus the calendar-scoping filters. Includes
 *  `highlightedInstanceKey` for surfaces that ring an event themselves; a
 *  `highlightedEvent` takes precedence over it. */
type CanvasCalendarBodyGridProps = Partial<
  Omit<
    ComponentProps<typeof CalendarView>,
    | "viewType"
    | "currentDate"
    | "onDateChange"
    | "scrollToNowKey"
    | "initialScrollTime"
    | "scrollToTimeKey"
    | "eventDragFcProps"
  >
>;

/**
 * The single calendar body shared by home and the thread canvas, so the grid
 * lays out identically on both surfaces. Bakes in the grid-area wrapper and reads
 * the shared nav (`view`/`date`/scroll-to-now) so callers only supply the surface
 * wiring. `calendarEditable` toggles drag-to-reschedule (`EditableCalendarView`
 * vs `CalendarView`) — a provably layout-neutral branch, since the editable
 * wrapper only adds portalled drag ghosts. Home and the thread canvas both pass
 * `true`; the flag is kept explicit so any future read-only surface has to opt
 * in deliberately rather than inheriting a default.
 *
 * Owns **event focus** as a general capability: `highlightedEvent` rings the
 * event's chip and jumps the view to its day — nothing thread-specific about it;
 * the caller supplies the event. Floating overlays that anchor to the grid
 * (home's popovers) come via the `overlay` slot + `containerRef`.
 */
export const CanvasCalendarBody = ({
  calendarEditable,
  highlightedEvent,
  gridProps,
  overlay,
  containerRef,
  ...surface
}: CanvasCalendarBodyGridProps & {
  calendarEditable: boolean;
  /** An event to ring on the calendar; jumps the view to its day. */
  highlightedEvent?: CalendarEvent;
  /** Selection/grid wiring from `useCalendarSurface` (spread onto the grid). */
  gridProps?: CanvasCalendarBodyGridProps;
  /** Portalled/floating overlays scoped to the grid (home's popovers). */
  overlay?: ReactNode;
  /** The wrapper element, exposed so overlays can anchor against it. */
  containerRef?: RefObject<HTMLDivElement | null>;
}) => {
  const { currentDate, setCurrentDate, viewType } = useCanvasCalendarNav();
  const scrollToNowKey = useScrollToNowKey();
  const scrollToTime = useScrollToTimeRequest();

  // Grid-anchored overlays portal into the wrapper; mirror the node to the
  // caller's ref too (home anchors its popovers against the same element).
  const innerRef = useRef<HTMLDivElement>(null);
  const setWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (containerRef) containerRef.current = node;
    },
    [containerRef],
  );

  const focusedInstanceKey = highlightedEvent?.instanceKey;
  const highlightedDateTime = highlightedEvent?.start?.dateTime;

  // Jump to the focused event's day so the ring lands on a painted chip.
  // Keyed on the date string, not the event: a refetch remints the event object
  // and would re-jump the view out from under a user who has since navigated.
  useEffect(() => {
    if (highlightedDateTime) setCurrentDate(parseISO(highlightedDateTime));
  }, [highlightedDateTime, setCurrentDate]);

  const props: Omit<ComponentProps<typeof CalendarView>, "eventDragFcProps"> = {
    viewType,
    currentDate,
    onDateChange: setCurrentDate,
    scrollToNowKey,
    ...surface,
    ...gridProps,
    // A focused event owns the ring while it's up; otherwise the caller's plain
    // highlight (a deep-linked event, ringed by the surface itself) does.
    highlightedInstanceKey:
      focusedInstanceKey ?? gridProps?.highlightedInstanceKey ?? surface.highlightedInstanceKey,
    // Owned here rather than passed in: a focus link's scroll target is shared
    // state, so both canvas surfaces honor it without the page wiring it through.
    initialScrollTime: scrollToTime?.time,
    scrollToTimeKey: scrollToTime?.seq,
  };

  return (
    // `relative`/`overflow-hidden` scopes grid-anchored overlays to the panel.
    // No insets: the grid runs edge-to-edge. Identical on both surfaces.
    <div
      ref={setWrapperRef}
      className="relative flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden"
    >
      {/* Extra flex-col wrapper so the grid's outer `flex-1` has a proper flex
          column to fill — without it the grid collapses, leaving dead space. */}
      <div className="flex-1 min-h-0 w-full flex flex-col">
        {calendarEditable ? <EditableCalendarView {...props} /> : <CalendarView {...props} />}
      </div>
      {overlay}
    </div>
  );
};
